import React from 'react';

import { RenderHtml } from '@blockworks/ui/legacy/string';
import type { PostType } from '@blockworks/platform/api/strapi';

import AdContainer from '@/components/ads/ad-container';
import AdContainerIds from '@/components/ads/ad-container-ids';
import Breadcrumb from '@/components/display/headers/breadcrumb';
import FeaturedWebinar from '@/components/pages/frontpage/components/feature/right/featured-webinar';
import Newsletter from '@/components/pages/frontpage/components/feature/right/newsletter';
import ResearchTeaser from '@/components/pages/frontpage/components/feature/right/research-teaser';
import type { Event, WebinarStripped } from '@/types/article-type';
import decodeEntities from '@/utils/functions/string/decode-entities';
import subscribeToNewsletter from '@/utils/functions/subscribe-to-newsletter';
import type { WordpressPage } from '@/utils/functions/wordpress/fetch-pages';
import UpcomingEvents from '@/components/sections/right/upcoming-events';

type WordpressPageLayoutProps = {
    page: WordpressPage;
    upcomingEvents?: Event[];
    featuredResearchPost?: PostType | null;
    featuredWebinar?: WebinarStripped | null;
};

const WordpressPageLayout = (props: WordpressPageLayoutProps): JSX.Element => {
    const { page, upcomingEvents, featuredWebinar, featuredResearchPost } = props;

    return (
        <main className="border-gray-200 w-full">
            <section className="flex flex-row flex-wrap xl:flex-nowrap">
                <div className="w-full lg:w-8/12 h-full p-4 md:p-8 lg:p-10 border-r-0 lg:border-r border-b lg:border-b-0">
                    {page.title && (
                        <div className="p-6 lg:p-12 border-b">
                            <Breadcrumb
                                items={[
                                    {
                                        title: page.title!.rendered || '',
                                        link: `/${page.slug}`,
                                    },
                                ]}
                            />
                            <div className="">
                                <h1 className="self-stretch w-full text-4xl lg:text-5xl font-semibold text-left text-[#110b29]">
                                    {decodeEntities(page.title!.rendered || '')}
                                </h1>
                            </div>
                        </div>
                    )}
                    <article className="p-6 lg:p-12">
                        <RenderHtml
                            rehypePlugins={[]}
                            className="prose prose-purple max-w-none prose-headings:scroll-mt-28 prose-headings:font-display prose-headings:font-normal lg:prose-headings:scroll-mt-[8.5rem] prose-lead:text-purple-500 prose-a:font-base prose-a:border-none prose-a:hover:border-none prose-a:underline prose-pre:rounded-xl prose-pre:bg-purple-900 prose-pre:shadow-lg"
                            html={page.content?.rendered || ''}
                        />
                    </article>
                </div>
                <div className="w-full basis-1/1 lg:basis-2/6">
                    <div>
                        <Newsletter
                            title="Newsletter"
                            subTitle="Blockworks Daily"
                            inputPlaceholder="Email address"
                            buttonText="Subscribe"
                            onButtonClick={subscribeToNewsletter}
                        />
                    </div>
                    <div>
                        <UpcomingEvents title="Upcoming Events" events={upcomingEvents} />
                    </div>
                    <div className="border-t md:border-l md:border-t-0 lg:border-l-0 lg:border-t w-full">
                        <div
                            className="relative lg:sticky top-8 justify-self-start px-12"
                            style={{ alignSelf: 'start' }}
                        >
                            <AdContainer
                                googleAd={AdContainerIds.NewsArticleRight600x300!}
                                className="invisible xl:visible h-0 w-0 xl:h-[600px] xl:w-[300px] xl:mt-8 text-center align-middle p-6"
                            />
                        </div>
                    </div>

                    {featuredResearchPost && (
                        <ResearchTeaser
                            headerTitle="Latest Research"
                            article={featuredResearchPost}
                            className="md:w-1/2 lg:w-full p-10"
                        />
                    )}

                    {featuredWebinar && (
                        <FeaturedWebinar
                            title={featuredWebinar.title}
                            smallHeadline="upcoming roundtable"
                            url={`/roundtable/${featuredWebinar.slug}`}
                            showMoreUrl={`/roundtable/${featuredWebinar.slug}`}
                            description={featuredWebinar.excerpt}
                            sponsoredBy={featuredWebinar.sponsoredBy}
                            date={featuredWebinar.date}
                            className="border-l lg:border-l-0 border-t md:border-t-0 lg:border-t border-b-0 md:w-1/2 lg:w-full"
                        />
                    )}
                </div>
            </section>
        </main>
    );
};

export default WordpressPageLayout;
